import { useEffect, useState } from "react";
import axios from "axios";
import { Layers, Loader2, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkSearchModal({ open, onClose, onResults, businessTypes = [] }) {
  const { t } = useLang();
  const [cities, setCities] = useState("Orlando, FL\nMiami, FL\nTampa, FL");
  const [businessType, setBusinessType] = useState("");
  const [keyword, setKeyword] = useState("");
  const [radius, setRadius] = useState("20km");
  const [autoSave, setAutoSave] = useState(false);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) {
      setSummary(null);
      setRunning(false);
    }
  }, [open]);

  if (!open) return null;

  const parseCities = (raw) => {
    // Split by newline OR by ",, " (double comma) so users can paste with single commas inside city names
    const items = raw.split(/\n|;/).map((s) => s.trim()).filter(Boolean);
    if (items.length > 1) return items;
    // Fallback: split by " · " or "|"
    return raw.split(/\||·/).map((s) => s.trim()).filter(Boolean);
  };

  const run = async (e) => {
    e?.preventDefault?.();
    const locations = parseCities(cities);
    if (locations.length === 0) return;
    setRunning(true);
    setSummary(null);
    try {
      const { data } = await axios.post(`${API}/bulk-search-leads`, {
        locations, business_type: businessType, keyword, radius,
        auto_save_qualified: autoSave,
      });
      setSummary({
        total: data.total_found,
        dedup: data.after_dedup,
        saved: data.auto_saved_qualified_ids?.length || 0,
        by_location: data.by_location || [],
      });
      if (onResults) onResults(data);
    } catch (e) {
      setSummary({ error: e?.response?.data?.detail || e?.message || "failed" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="bulk-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="al-card max-w-[640px] w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#3B82F6]" />
            <h2 className="text-[16px] font-bold text-[#111827]">{t("bulk.modal_title")}</h2>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827]" data-testid="bulk-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={run} className="space-y-3">
          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1">{t("bulk.cities_label")}</label>
            <textarea
              data-testid="bulk-cities-input"
              rows={4}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-[13px] font-mono bg-white focus:outline-none focus:border-[#3B82F6]"
              placeholder={t("bulk.cities_placeholder")}
              value={cities}
              onChange={(e) => setCities(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1">Type</label>
              <select
                data-testid="bulk-business-type"
                className="w-full h-9 px-2 border border-[#E5E7EB] rounded-lg text-[13px] bg-white"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
              >
                <option value="">All</option>
                {businessTypes.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#374151] mb-1">Radius</label>
              <select
                data-testid="bulk-radius"
                className="w-full h-9 px-2 border border-[#E5E7EB] rounded-lg text-[13px] bg-white"
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
              >
                {["5km", "10km", "20km", "30km", "50km"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[#374151] mb-1">Keyword (optional)</label>
            <input
              data-testid="bulk-keyword"
              className="w-full h-9 px-3 border border-[#E5E7EB] rounded-lg text-[13px] bg-white focus:outline-none focus:border-[#3B82F6]"
              placeholder="waterproofing, roofing, ..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>

          <label className="inline-flex items-center gap-2 text-[12px] text-[#374151]">
            <input
              type="checkbox"
              data-testid="bulk-auto-save"
              checked={autoSave}
              onChange={(e) => setAutoSave(e.target.checked)}
            />
            {t("bulk.auto_save")}
          </label>

          <button
            type="submit"
            data-testid="bulk-run-btn"
            disabled={running}
            className="al-btn-primary flex items-center justify-center gap-2 w-full h-10 rounded-lg text-[13px] font-semibold disabled:opacity-60"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            {t("bulk.run_button")}
          </button>
        </form>

        {summary && !summary.error && (
          <div data-testid="bulk-results" className="mt-4 p-3 bg-[#EFF6FF] rounded-lg text-[12px] text-[#1E40AF]">
            <div className="font-semibold mb-1">
              {t("bulk.results_summary", { total: summary.total, dedup: summary.dedup, saved: summary.saved })}
            </div>
            <ul className="space-y-0.5 text-[11px]">
              {summary.by_location.map((bl) => (
                <li key={bl.location}>
                  • <strong>{bl.location}</strong>: {bl.count} ({bl.source})
                  {bl.error ? <span className="text-[#DC2626]"> · {bl.error}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        )}
        {summary?.error && (
          <div className="mt-4 p-3 bg-[#FEE2E2] rounded-lg text-[12px] text-[#DC2626]">{summary.error}</div>
        )}
      </div>
    </div>
  );
}
